import React, { useEffect, useState, useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Shield, Scale, Phone, Menu, X, Star, ChevronRight,
  Siren, Gavel, FileWarning, Car, Users,
  Landmark, AlertTriangle, Ban, ShieldAlert, Fingerprint,
  BookOpen, UserX, Repeat, MessageCircle, Instagram,
  Lock, KeyRound, Pill, Home, Search, Wine, ArrowUpRight, Link2
} from "lucide-react";

const WHATSAPP_NUMBER = "5521992187976";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Ol%C3%A1%2C%20preciso%20de%20assist%C3%AAncia%20jur%C3%ADdica.`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dra-ana-defesa/artifacts/q2px57cl_Logotipo%20Verde%20Advocacia.png";
const INSTAGRAM_URL = "https://www.instagram.com/draanaleandroadvogada?igsh=ZmpsNHBmZ2o1Mjdh";

const CLIENT_PHOTO_HERO = "https://customer-assets.emergentagent.com/job_dra-ana-defesa/artifacts/207aniw2_55A7B9DD-4332-4C25-9423-891F5BB760E9.png";
const CLIENT_PHOTO_ABOUT = "https://customer-assets.emergentagent.com/job_dra-ana-defesa/artifacts/pn01v8xt_Gemini_Generated_Image_kk7z91kk7z91kk7z.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_dra-ana-defesa/artifacts/207aniw2_55A7B9DD-4332-4C25-9423-891F5BB760E9.png";
const TESTIMONIAL_BG = "https://images.unsplash.com/photo-1612627391400-f2ff07667797?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxwcmlzb24lMjBiYXJzJTIwc2hhZG93JTIwZGFya3xlbnwwfHx8fDE3NzU5NjQ4Mzl8MA&ixlib=rb-4.1.0&q=85";

/* ─── Animated Section Wrapper ─── */
function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── HEADER ─── */
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "Sobre", href: "#sobre" },
    { label: "Especialidades", href: "#especialidades" },
    { label: "Depoimentos", href: "#depoimentos" },
    { label: "Contato", href: "#contato" },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/80 border-b border-gold-900/30 shadow-lg shadow-black/30"
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-24 md:h-28">
        {/* Logo */}
        <a href="#hero" className="flex items-center gap-3 group" data-testid="header-logo">
          <img src={LOGO_URL} alt="Dra. Ana Leandro - Advocacia Criminal" className="h-20 md:h-28 w-auto" />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium tracking-wide uppercase text-white/70 hover:text-gold-300 transition-colors duration-300 font-body"
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-gold-600 hover:bg-gold-700 text-black px-5 py-2.5 rounded-sm text-sm font-semibold tracking-wide uppercase transition-all duration-300 hover:scale-105 animate-gold-pulse font-body btn-gold-shine"
            data-testid="header-whatsapp-btn"
          >
            <Phone className="w-4 h-4" />
            Plantão 24h
          </a>
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-btn"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-gold-900/30"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-base font-medium text-white/80 hover:text-gold-300 transition-colors font-body py-2"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-gold-600 text-black px-5 py-3 rounded-sm text-sm font-semibold uppercase mt-2 font-body"
              data-testid="mobile-whatsapp-btn"
            >
              <Phone className="w-4 h-4" />
              Plantão 24h
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}

/* ─── HERO ─── */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-end overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full background image */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img
          src={HERO_BG}
          alt="Dra. Ana Leandro - Advogada Criminalista"
          className="w-full h-[120%] object-cover object-[65%_top] md:object-[center_top]"
          data-testid="hero-bg-image"
        />
        {/* Dark overlays - stronger on mobile for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40 md:from-black/80 md:via-black/50 md:to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/10 md:via-black/30 md:to-transparent" />
        <div className="absolute inset-0 bg-black/15 md:bg-black/20" />
      </motion.div>

      {/* Red siren glow effects */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full bg-gold-600/5 blur-[120px] animate-siren pointer-events-none" />
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full bg-gold-600/3 blur-[100px] animate-siren pointer-events-none" style={{ animationDelay: "1s" }} />

      <motion.div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full pb-8 md:pb-24 pt-28 md:pt-32" style={{ y: textY }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-2xl"
        >
          <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-gold-300 font-body font-bold mb-3 md:mb-4 block" data-testid="hero-overline">
            Defesa Criminal Especializada
          </span>

          {/* Atendimento 24h badge - highlighted */}
          <div className="inline-flex items-center gap-2 bg-gold-600 text-black px-4 py-2 rounded-sm mb-5 md:mb-6 animate-gold-pulse" data-testid="hero-24h-badge">
            <Phone className="w-4 h-4 animate-pulse" />
            <span className="text-sm md:text-base font-body font-extrabold uppercase tracking-widest">Atendimento 24h</span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[0.95] mb-4 md:mb-6 tracking-tight" data-testid="hero-title">
            Sua Liberdade<br />
            <span className="text-gold-300">e Sua Reputação</span><br />
            São Prioridade
          </h1>
          <p className="text-sm md:text-lg text-white/70 leading-relaxed max-w-lg mb-6 md:mb-8 font-body" data-testid="hero-description">
            Atendimento 24 horas. Atuação imediata em prisões em flagrante, mandados de busca, e todas as emergências criminais.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-gold-600 hover:bg-gold-700 text-black px-6 md:px-8 py-3.5 md:py-4 rounded-sm text-sm md:text-base font-bold uppercase tracking-wider transition-all duration-300 hover:scale-105 animate-gold-pulse font-body whitespace-nowrap btn-gold-shine"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Fale Agora no WhatsApp
            </a>
            <a
              href="#especialidades"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold-600/50 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-sm text-sm md:text-base font-medium uppercase tracking-wider transition-all duration-300 hover:bg-white/5 backdrop-blur-sm font-body whitespace-nowrap"
              data-testid="hero-expertise-btn"
            >
              Nossas Especialidades
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Stats */}
          <div className="flex gap-6 md:gap-8 mt-8 md:mt-10 pt-6 md:pt-8 border-t border-white/15">
            <div>
              <span className="text-2xl md:text-3xl font-heading font-bold text-gold-300">24h</span>
              <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/50 mt-1 font-body">Atendimento</p>
            </div>
            <div>
              <span className="text-2xl md:text-3xl font-heading font-bold text-white">18+</span>
              <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/50 mt-1 font-body">Especialidades</p>
            </div>
            <div>
              <span className="text-2xl md:text-3xl font-heading font-bold text-white flex items-center gap-1">5.0 <Star className="w-4 h-4 md:w-5 md:h-5 text-gold-300 fill-gold-300" /></span>
              <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/50 mt-1 font-body">Avaliação</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050505] to-transparent z-10 pointer-events-none" />
    </section>
  );
}

/* ─── MARQUEE ─── */
function MarqueeSection() {
  const areas = [
    "Prisão em Flagrante", "Habeas Corpus", "Tráfico de Drogas", "Homicídio",
    "Lei Maria da Penha", "Crimes Federais", "Crimes Sexuais", "Lei Seca",
    "Audiência de Custódia", "Progressão de Regime", "Prisão Domiciliar",
    "Inquéritos Policiais", "Reabilitação Criminal", "Crimes contra o Patrimônio",
    "Defesa Criminal Corporativa", "Delitos e Contravenções"
  ];

  return (
    <div className="relative py-4 md:py-6 bg-surface/50 border-y border-white/5 overflow-hidden" data-testid="marquee-section">
      <Marquee speed={30} gradient={false} pauseOnHover>
        {areas.map((area, i) => (
          <span key={i} className="flex items-center gap-3 md:gap-4 mx-4 md:mx-6">
            <span className="text-xs md:text-sm font-body font-semibold uppercase tracking-widest text-white/50 whitespace-nowrap">
              {area}
            </span>
            <span className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gold-600/60" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* ─── ABOUT ─── */
function About() {
  return (
    <section id="sobre" className="relative py-14 md:py-28 noise-overlay" data-testid="about-section">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
          {/* Image */}
          <AnimatedSection>
            <div className="relative" data-testid="about-photo-container">
              <div className="absolute -inset-6 bg-gold-600/5 blur-3xl rounded-full" />
              <div className="relative overflow-hidden rounded-sm border border-white/10">
                <img
                  src={CLIENT_PHOTO_ABOUT}
                  alt="Dra. Ana Leandro"
                  className="w-full aspect-[4/5] md:aspect-[3/4] object-cover object-top"
                  data-testid="about-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>
              {/* Red corner accents */}
              <div className="absolute -top-2 -right-2 w-16 h-1 bg-gold-600" />
              <div className="absolute -top-2 -right-2 w-1 h-16 bg-gold-600" />
            </div>
          </AnimatedSection>

          {/* Text */}
          <AnimatedSection delay={0.2}>
            <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-gold-300 font-body font-bold mb-3 md:mb-4 block" data-testid="about-overline">
              Sobre o Escritório
            </span>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 tracking-tight leading-tight" data-testid="about-title">
              Dra. Ana Leandro
            </h2>
            <div className="w-12 md:w-16 h-1 bg-gold-600 mb-6 md:mb-8" />
            <p className="text-sm md:text-lg text-white/70 leading-relaxed font-body mb-4 md:mb-6" data-testid="about-text">
              Especializado em Direito Penal, nosso escritório atua com agilidade, discrição e estratégia na defesa de pessoas acusadas ou investigadas por crimes ou vítimas. Atendemos casos 24h, como prisão em flagrante, mandado de busca, tornozeleira eletrônica, tráfico de drogas, violência doméstica, homicídio, entre outros.
            </p>
            <p className="text-sm md:text-lg text-white/70 leading-relaxed font-body mb-6 md:mb-8" data-testid="about-text-2">
              Nosso compromisso é oferecer orientação segura, acolhimento e uma atuação técnica sólida desde os primeiros momentos da crise, seja na delegacia, audiência ou processo. Aqui, você não está sozinho — atuamos com seriedade para proteger o que há de mais importante: <span className="text-gold-300 font-semibold">sua liberdade e sua reputação.</span>
            </p>

            <div className="flex flex-wrap gap-3 md:gap-4">
              <div className="flex items-center gap-2 md:gap-3 bg-surface/80 border border-white/5 px-4 md:px-5 py-2.5 md:py-3 rounded-sm">
                <Shield className="w-4 h-4 md:w-5 md:h-5 text-gold-300" />
                <span className="text-xs md:text-sm font-body text-white/80 font-medium">Agilidade</span>
              </div>
              <div className="flex items-center gap-2 md:gap-3 bg-surface/80 border border-white/5 px-4 md:px-5 py-2.5 md:py-3 rounded-sm">
                <Scale className="w-4 h-4 md:w-5 md:h-5 text-gold-300" />
                <span className="text-xs md:text-sm font-body text-white/80 font-medium">Estratégia</span>
              </div>
              <div className="flex items-center gap-2 md:gap-3 bg-surface/80 border border-white/5 px-4 md:px-5 py-2.5 md:py-3 rounded-sm">
                <Gavel className="w-4 h-4 md:w-5 md:h-5 text-gold-300" />
                <span className="text-xs md:text-sm font-body text-white/80 font-medium">Discrição</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

/* ─── EXPERTISE ─── */
const expertiseItems = [
  { icon: Link2, label: "Prisão em Flagrante" },
  { icon: Gavel, label: "Audiência de Custódia" },
  { icon: KeyRound, label: "Pedido de Liberdade ou Habeas Corpus" },
  { icon: Pill, label: "Tráfico de Drogas" },
  { icon: ShieldAlert, label: "Lei Maria da Penha / Violência Doméstica" },
  { icon: AlertTriangle, label: "Crimes contra o Patrimônio (roubo, furto, estelionato)" },
  { icon: Shield, label: "Homicídio e Outras Situações Graves" },
  { icon: UserX, label: "Crimes Sexuais" },
  { icon: BookOpen, label: "Reabilitação Criminal" },
  { icon: Search, label: "Defesa em Investigações e Inquéritos Policiais" },
  { icon: Wine, label: "Lei Seca" },
  { icon: Home, label: "Pedido de Prisão Domiciliar" },
  { icon: ArrowUpRight, label: "Progressão de Regime" },
  { icon: Landmark, label: "Defesa Criminal Corporativa" },
  { icon: Car, label: "Direção Perigosa e sob Influência" },
  { icon: Landmark, label: "Crimes Federais" },
  { icon: Lock, label: "Defesa Criminal" },
  { icon: FileWarning, label: "Delitos e Contravenções" },
];

function Expertise() {
  return (
    <section id="especialidades" className="relative py-14 md:py-28" data-testid="expertise-section">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-surface/30 to-[#050505]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-10 md:mb-16">
          <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-gold-300 font-body font-bold mb-3 md:mb-4 block" data-testid="expertise-overline">
            Áreas de Atuação
          </span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="expertise-title">
            Especialidades
          </h2>
          <div className="w-12 md:w-16 h-1 bg-gold-600 mx-auto mt-4 md:mt-6" />
        </AnimatedSection>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4" data-testid="expertise-grid">
          {expertiseItems.map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.03}>
              <motion.div
                className="group relative bg-[#121212] border border-white/5 rounded-sm p-4 md:p-6 transition-all duration-500 hover:border-gold-900/50 cursor-default h-full"
                whileHover={{ y: -4 }}
                data-testid={`expertise-card-${i}`}
              >
                <div className="absolute inset-0 bg-gold-600/0 group-hover:bg-gold-600/5 transition-all duration-500 rounded-sm" />
                <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-4">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-sm bg-gold-600/10 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-600/20 transition-colors duration-300">
                    <item.icon className="w-4 h-4 md:w-5 md:h-5 text-gold-300" />
                  </div>
                  <span className="text-xs md:text-sm font-body font-medium text-white/80 group-hover:text-white transition-colors duration-300 leading-snug text-center md:text-left md:pt-2">
                    {item.label}
                  </span>
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ─── */
const GOOGLE_REVIEWS_URL = "https://share.google/N5IUEeusaipKn5Nmy";

const testimonials = [
  {
    name: "Tassiany Batista",
    initial: "T",
    reviews: "8 avaliações",
    text: "Uma excelente profissional, sempre atende seus clientes com empatia! Uma profissional que resolve seu problema de verdade.",
  },
  {
    name: "Rosana Herminio",
    initial: "R",
    reviews: "3 avaliações",
    text: "Profissional extremamente competente, atenciosa e preparada. Desde o primeiro contato, me senti acolhida e orientada com clareza sobre todos os passos do processo. Explica tudo com paciência, sem juridiquês, e mostra total comprometimento com o caso. Uma advogada que transmite segurança e realmente faz a diferença em momentos difíceis. Recomendo de olhos fechados.",
  },
  {
    name: "Christiano Pereira Chris",
    initial: "C",
    reviews: "8 avaliações",
    text: "Excelente ser humano e profissional melhor ainda. Já precisei várias vezes da Dra Ana Leandro em atuação criminal à clientes meus. Em todas as ocasiões ela foi altamente qualificada, eficaz e cirúrgica. Recomendo fortemente.",
  },
  {
    name: "Rayane Marques",
    initial: "R",
    reviews: "28 avaliações",
    badge: "Local Guide",
    text: "Excelente advogada! Preparadíssima e sempre disponível para ajudar. O número da Dra Ana está salvo nos meus contatos de emergência! Sei que posso confiar.",
  },
  {
    name: "Vinicius Morais",
    initial: "V",
    reviews: "11 avaliações",
    text: "Excelente profissional! Fui atendido pela Dra. Ana Leandro em um momento delicado e desde o primeiro contato ela demonstrou total domínio jurídico, empatia e comprometimento. Explicou cada passo com clareza, sempre com ética e transparência. Sua atuação foi firme, técnica e extremamente eficiente. Recomendo com total confiança.",
  },
  {
    name: "Marcelo Corrêa",
    initial: "M",
    reviews: "89 avaliações",
    badge: "Local Guide",
    text: "Ótima advogada. Recomendo a todos enrolados com algum tipo de situação que envolva área criminal. Se tiver problema, bico calado e liga pra ela.",
  },
  {
    name: "Yvana Sodré",
    initial: "Y",
    reviews: "7 avaliações",
    text: "Com a Dra Ana, a gente nunca sai perdendo! Excelente profissional!! Super indico.",
  },
];

function Testimonials() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const t = testimonials[current];

  return (
    <section id="depoimentos" className="relative py-14 md:py-28 overflow-hidden" data-testid="testimonials-section">
      {/* Prison bars background */}
      <div className="absolute inset-0 z-0">
        <img
          src={TESTIMONIAL_BG}
          alt=""
          className="w-full h-full object-cover opacity-[0.06]"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-gold-900/5 via-transparent to-gold-900/5" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-10 md:mb-16">
          <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-gold-300 font-body font-bold mb-3 md:mb-4 block" data-testid="testimonials-overline">
            Depoimentos
          </span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="testimonials-title">
            O Que Dizem Nossos Clientes
          </h2>
          <div className="w-12 md:w-16 h-1 bg-gold-600 mx-auto mt-4 md:mt-6" />
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="max-w-3xl mx-auto">
            {/* Rating */}
            <div className="flex items-center justify-center gap-2 mb-6 md:mb-8" data-testid="testimonials-rating">
              <span className="text-3xl md:text-4xl font-heading font-bold text-white">5,0</span>
              <div className="flex gap-1 ml-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 md:w-6 md:h-6 text-gold-300 fill-gold-300" />
                ))}
              </div>
            </div>

            {/* Testimonial card - animated */}
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5 }}
              className="relative bg-[#121212]/80 backdrop-blur-md border border-white/5 rounded-sm p-6 md:p-10"
              data-testid="testimonial-card"
            >
              <div className="absolute -top-4 left-6 md:left-8 text-5xl md:text-6xl font-heading text-gold-300/30 leading-none">&ldquo;</div>

              <p className="text-sm md:text-lg text-white/80 leading-relaxed font-body italic mb-6 pt-4" data-testid="testimonial-text">
                {t.text}
              </p>

              <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gold-600/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-base md:text-lg font-heading font-bold text-gold-300">{t.initial}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base font-body font-semibold text-white" data-testid="testimonial-author">{t.name}</p>
                  <p className="text-xs md:text-sm font-body text-white/40">
                    {t.badge && <span className="text-gold-300/70 mr-1">{t.badge} · </span>}
                    {t.reviews}
                  </p>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-300 fill-gold-300" />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Dots navigation */}
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="testimonial-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === current ? "bg-gold-300 w-6" : "bg-white/20 hover:bg-white/40"
                  }`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>

            {/* Google Reviews link */}
            <div className="text-center mt-8">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm md:text-base font-body font-medium text-white/50 hover:text-gold-300 transition-colors duration-300 border border-white/10 hover:border-gold-600/30 px-6 py-3 rounded-sm"
                data-testid="google-reviews-link"
              >
                <Star className="w-4 h-4 text-gold-300 fill-gold-300" />
                Ver Todas as Avaliações no Google
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ─── CTA SECTION ─── */
function CtaSection() {
  return (
    <section id="contato" className="relative py-14 md:py-28 overflow-hidden" data-testid="cta-section">
      {/* Background glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[800px] h-[300px] md:h-[400px] bg-gold-600/8 blur-[150px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
        <AnimatedSection>
          <Siren className="w-10 h-10 md:w-12 md:h-12 text-gold-300 mx-auto mb-4 md:mb-6 animate-float" />
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 md:mb-6" data-testid="cta-title">
            Precisa de Defesa <span className="text-gold-300">Agora?</span>
          </h2>
          <p className="text-sm md:text-lg text-white/60 leading-relaxed font-body mb-8 md:mb-10 max-w-2xl mx-auto" data-testid="cta-text">
            Não espere. Cada minuto conta quando se trata da sua liberdade. Entre em contato agora mesmo e receba atendimento imediato.
          </p>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-gold-600 hover:bg-gold-700 text-black px-8 md:px-10 py-4 md:py-5 rounded-sm text-sm md:text-lg font-bold uppercase tracking-wider transition-all duration-300 hover:scale-105 animate-gold-pulse font-body btn-gold-shine"
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
            Fale com a Dra. Ana Leandro
          </a>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  return (
    <footer className="relative bg-black border-t border-white/5 py-8 md:py-12 pb-20 md:pb-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Dra. Ana Leandro" className="h-12 md:h-16 w-auto" />
          </div>

          <p className="text-xs md:text-sm text-white/30 font-body text-center" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} Dra. Ana Leandro — Todos os direitos reservados.
          </p>

          <div className="flex items-center gap-5">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-body font-medium transition-colors"
              data-testid="footer-instagram-btn"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gold-300 hover:text-gold-200 text-sm font-body font-medium transition-colors"
              data-testid="footer-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── FLOATING WHATSAPP ─── */
function FloatingWhatsApp() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <motion.a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0 }}
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ scale: { repeat: Infinity, duration: 2, ease: "easeInOut" } }}
      className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 w-14 h-14 md:w-16 md:h-16 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center shadow-lg shadow-green-900/40 hover:scale-110"
      data-testid="floating-whatsapp-btn"
    >
      <MessageCircle className="w-7 h-7 text-white" />
      {/* Ping ring */}
      <span className="absolute inset-0 rounded-full bg-green-500/30 animate-ping" style={{ animationDuration: "2s" }} />
    </motion.a>
  );
}

/* ─── MAIN APP ─── */
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      <Header />
      <Hero />
      <MarqueeSection />
      <About />
      <Expertise />
      <Testimonials />
      <CtaSection />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
